import { Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ILeadAPIResponse,
  ILeadSearchFilter,
  IUserDTO,
  getAllLeadTags,
  getAllLeads,
} from 'wisestep-api';

import { useUserContext } from '../../../context/UserContext';
import { leadTypes } from '../../../features/notes/helper/NotesHelper';
import getDecodedQueryParams from '../../../utils/getDecodedQueryParams';
import { defaultLeadSearchFilter, mapToILeadAdvanceFilter } from '../helper/leadHelper';

import BulkLeadOwnerUpdateDrawer from './BulkLeadOwnerUpdateDrawer';
import CommonHeader from './CommonHeader';
import LeadAdvanceFilters from './LeadAdvanceFilters';
import LeadData from './LeadData';
import LeadFilters from './LeadFilters';
import RandomAssignmentDrawer from './RandomAssignmentDrawer';

const LeadList = () => {
  const [param] = useSearchParams();
  const queryData = param.get('d') || '';
  const getDecodedFilters = useCallback((): ILeadSearchFilter => {
    if (!queryData) return defaultLeadSearchFilter;
    try {
      return getDecodedQueryParams<ILeadSearchFilter>(queryData) ?? defaultLeadSearchFilter;
    } catch (error) {
      console.error('Failed to decode query params:', error);
      return defaultLeadSearchFilter;
    }
  }, [queryData]);
  const [openAdvancedFilters, setOpenAdvancedFilters] = useState<boolean>(false);
  const [leads, setLeads] = useState<ILeadAPIResponse>();
  const [leadType, setLeadType] = useState<string>('all_leads');
  const [searchQuery, setSearchQuery] = useState<ILeadSearchFilter>(getDecodedFilters);
  const [advancedFilterDefaults, setAdvancedFilterDefaults] =
    useState<ILeadSearchFilter>(searchQuery);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [parentSelectedLeadIds, setParentSelectedLeadIds] = useState<string[]>([]);
  const anyLeadsSelected = parentSelectedLeadIds.length > 0;
  const [randomAssignDrawerOpen, setRandomAssignDrawerOpen] = useState(false);
  const [bulkUpdateDrawerOpen, setBulkUpdateDrawerOpen] = useState(false);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const leadsData = await getAllLeads(searchQuery);
        setLeads(leadsData?.data || null);
      } catch (error) {
        console.error('Error fetching leads:', error);
      }
    };
    fetchLeads();
  }, [searchQuery]);

  useEffect(() => {
    const fetchTags = async () => {
      const tagsRes = await getAllLeadTags();
      setTags(tagsRes.data);
    };

    fetchTags();
  }, []);

  const onClear = () => {
    setAdvancedFilterDefaults(defaultLeadSearchFilter);
    setSearchQuery(defaultLeadSearchFilter);
    setParentSelectedLeadIds([]);
  };

  const onFilterFormSubmit = (data: ILeadSearchFilter) => {
    setSearchQuery((prevQuery) => ({
      ...prevQuery,
      ...mapToILeadAdvanceFilter(data),
    }));
    setAdvancedFilterDefaults(data);
    setParentSelectedLeadIds([]);
  };

  const getLeadType = (leadType) => {
    setLeadType(leadType);
    const leadOwners = leadType === 'my_leads' ? [loggedInUser.email ?? ''] : [];
    setSearchQuery((prevQuery) => ({
      ...prevQuery,
      leadOwners: leadOwners,
    }));
    setParentSelectedLeadIds([]);
  };
  const handleBulkUpdate = () => {
    setParentSelectedLeadIds([]);
    setRandomAssignDrawerOpen(false);
    setBulkUpdateDrawerOpen(false);
    setSearchQuery((prevQuery) => ({ ...prevQuery }));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} pb={3}>
        <CommonHeader
          title={leadType === 'my_leads' ? 'My Leads' : 'All Leads'}
          onSelect={getLeadType}
          options={leadTypes}
        />
      </Grid>
      {leads && (
        <Grid item xs={openAdvancedFilters ? 9 : 12}>
          <LeadFilters
            onAdvancedFiltersClick={() => setOpenAdvancedFilters(!openAdvancedFilters)}
            searchQuery={searchQuery ?? {}}
            getUpdatedFiltersFromFilters={(query) => setSearchQuery(query)}
            clear={onClear}
            anyLeadsSelected={anyLeadsSelected}
            onRandomAssign={() => setRandomAssignDrawerOpen(true)}
            onBulkAssign={() => setBulkUpdateDrawerOpen(true)}
            selectedLeadsCount={parentSelectedLeadIds.length}
          />
          <LeadData
            leads={leads}
            searchQuery={searchQuery ?? {}}
            getUpdatedFiltersFromTable={(query) => setSearchQuery(query)}
            leadType={leadType}
            onSelectionChange={(selectedIds: string[]) => setParentSelectedLeadIds(selectedIds)}
          />
        </Grid>
      )}
      {openAdvancedFilters && (
        <Grid item xs={3}>
          <LeadAdvanceFilters
            onClose={() => setOpenAdvancedFilters(false)}
            defaultValues={advancedFilterDefaults}
            onSubmit={onFilterFormSubmit}
            tags={tags}
          />
        </Grid>
      )}
      <RandomAssignmentDrawer
        open={randomAssignDrawerOpen}
        selectedLeadIds={parentSelectedLeadIds}
        onClose={() => setRandomAssignDrawerOpen(false)}
        onBulkUpdate={handleBulkUpdate}
        leadsData={[]}
      />
      <BulkLeadOwnerUpdateDrawer
        open={bulkUpdateDrawerOpen}
        selectedLeadIds={parentSelectedLeadIds}
        onClose={() => setBulkUpdateDrawerOpen(false)}
        onBulkUpdate={handleBulkUpdate}
        leadsData={[]}
      />
    </Grid>
  );
};

export default LeadList;
